import React from 'react';
import { Avatar } from 'flowbite-react';
import { FaCalendarAlt, FaClock, FaBoxOpen } from 'react-icons/fa';
import { IoNotificationsOutline } from 'react-icons/io5';
import acquisitionImage from '../../assets/acquisition.png';
import dispositionImage from '../../assets/disposition.png';

/**
 * RequestTypeAvatar
 * Renders an avatar for a given request type. Uses icons for Leave/Overtime and images for asset requests.
 *
 * Props:
 * - requestType: string (e.g., 'Leave', 'Overtime', 'Asset acquisition', 'Asset disposition', 'Supply Request')
 * - size: 'sm' | 'md' | 'lg' (default 'md')
 * - className: optional extra classes for outer element
 */
const RequestTypeAvatar = ({ requestType, size = 'md', className = '' }) => {
  const sizeMap = {
    sm: { wh: 'w-8 h-8', icon: 'w-4 h-4' },
    md: { wh: 'w-12 h-12', icon: 'w-6 h-6' },
    lg: { wh: 'w-14 h-14', icon: 'w-8 h-8' },
  };

  const { wh, icon } = sizeMap[size] || sizeMap.md;

  const config = (() => {
    switch (requestType) {
      case 'Asset acquisition':
        return { img: acquisitionImage, bg: 'bg-blue-100 dark:bg-blue-900' };
      case 'Asset disposition':
        return { img: dispositionImage, bg: 'bg-red-100 dark:bg-red-900' };
      case 'Leave':
        return { Icon: FaCalendarAlt, bg: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-200' };
      case 'Overtime':
        return { Icon: FaClock, bg: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-200' };
      case 'Supply Request':
        return { Icon: FaBoxOpen, bg: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-200' };
      default:
        return { Icon: IoNotificationsOutline, bg: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200' };
    }
  })();

  if (config.img) {
    // Use image-based avatar
    return (
      <Avatar
        rounded
        img={config.img}
        className={`rounded-full ${wh} object-cover aspect-square ${config.bg} ${className}`}
      />
    );
  }

  const Icon = config.Icon || IoNotificationsOutline;
  // Icon-based avatar inside a colored circle
  return (
    <div className={`flex items-center justify-center rounded-full ${wh} ${config.bg} ${className}`}>
      <Icon className={`${icon}`} />
    </div>
  );
};

export default RequestTypeAvatar;
